import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { updateCampaign } from '../../api/campaigns';
import {
  createProduct,
  updateProduct,
  deleteProduct,
  uploadProductImage,
  productImageUrl,
} from '../../api/products';
import { useCampaignWorkspace } from './CampaignWorkspaceContext';
import { ProductFormModal, VideoModal } from './campaignProductModals';

export default function CampaignProductsTab() {
  const { campaign, campaignId, reloadCampaign, canManageCampaigns } =
    useCampaignWorkspace();
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [videoToPlay, setVideoToPlay] = useState(null);

  const productList = Array.isArray(campaign?.products) ? campaign.products : [];

  const handleAddProduct = async (data) => {
    try {
      const createRes = await createProduct({
        name: data.name,
        description: data.description ?? '',
        videoLink: data.videoLink || null,
        price: typeof data.price === 'number' ? data.price : 0,
      });
      const newProduct = createRes?.data?.product;
      if (!newProduct) return;
      if (data.imageFile) {
        try {
          await uploadProductImage(newProduct._id, data.imageFile);
        } catch (e) {
          console.error(e);
        }
      }
      const currentIds = productList.map((p) => (typeof p === 'string' ? p : p._id));
      await updateCampaign(campaignId, { products: [...currentIds, newProduct._id] });
      await reloadCampaign();
      setShowProductForm(false);
    } catch (e) {
      console.error(e);
    }
  };

  const handleUpdateProduct = async (productId, data) => {
    try {
      await updateProduct(productId, {
        name: data.name,
        description: data.description ?? '',
        videoLink: data.videoLink || null,
        price: typeof data.price === 'number' ? data.price : 0,
      });
      if (data.imageFile) {
        try {
          await uploadProductImage(productId, data.imageFile);
        } catch (e) {
          console.error(e);
        }
      }
      await reloadCampaign();
      setEditingProduct(null);
    } catch (e) {
      console.error(e);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Retirer ce produit de la campagne ?')) return;
    setDeletingId(productId);
    try {
      await deleteProduct(productId);
      const currentIds = productList.map((p) => (typeof p === 'string' ? p : p._id));
      await updateCampaign(campaignId, {
        products: currentIds.filter((pid) => pid !== productId),
      });
      await reloadCampaign();
    } catch (e) {
      console.error(e);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Produits</h2>
          <p className="text-sm text-muted-foreground">
            Fiches produit utilisables dans vos emails (optionnel).
          </p>
        </div>
        {canManageCampaigns ? (
          <Button iconName="Plus" iconPosition="left" onClick={() => setShowProductForm(true)}>
            Nouveau produit
          </Button>
        ) : null}
      </div>

      {!campaign?.stancerKeysConfigured ? (
        <Link
          to="/crm-configuration"
          className="mb-4 inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 dark:text-amber-400 hover:underline"
        >
          <Icon name="CreditCard" size={14} />
          Paiement en ligne non configuré — bouton d&apos;achat dans les emails
        </Link>
      ) : (
        <p className="mb-4 inline-flex items-center gap-1.5 text-xs text-muted-foreground">
          <Icon name="CheckCircle" size={14} className="text-emerald-600" />
          Paiement Stancer actif
        </p>
      )}

      {productList.length === 0 ? (
        <div className="rounded-xl border border-border bg-card p-12 text-center">
          <Icon name="Package" size={48} className="mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-lg font-semibold text-foreground mb-2">Aucun produit</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Ajoutez des produits pour les proposer dans vos templates email.
          </p>
          {canManageCampaigns ? (
            <Button onClick={() => setShowProductForm(true)} iconName="Plus" iconPosition="left">
              Créer un produit
            </Button>
          ) : null}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {productList.map((product) => {
            const p = typeof product === 'object' ? product : { _id: product, name: '' };
            const img = productImageUrl(p);
            const showPrice = typeof p.price === 'number' && p.price > 0;
            return (
              <div
                key={p._id}
                className="flex h-full flex-col overflow-hidden rounded-xl border border-border bg-card shadow-elevation-1 transition-smooth hover:shadow-elevation-2"
              >
                <div className="relative aspect-[4/3] bg-muted/40">
                  {img ? (
                    <img src={img} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full items-center justify-center text-muted-foreground">
                      <Icon name="Package" size={32} className="opacity-40" />
                    </div>
                  )}
                  {canManageCampaigns ? (
                    <div className="absolute top-2 right-2 flex gap-1">
                      <button
                        type="button"
                        className="rounded-md bg-background/90 p-1.5 shadow-sm hover:bg-muted"
                        onClick={() => setEditingProduct(p)}
                        aria-label="Modifier"
                      >
                        <Icon name="Pencil" size={14} className="text-muted-foreground" />
                      </button>
                      <button
                        type="button"
                        className="rounded-md bg-background/90 p-1.5 shadow-sm hover:bg-destructive/10"
                        onClick={() => handleDeleteProduct(p._id)}
                        disabled={deletingId === p._id}
                        aria-label="Supprimer"
                      >
                        <Icon name="Trash2" size={14} className="text-error" />
                      </button>
                    </div>
                  ) : null}
                </div>
                <div className="flex flex-1 flex-col p-4">
                  <div className="mb-2 flex items-start justify-between gap-2">
                    <h3 className="line-clamp-2 flex-1 text-base font-semibold text-foreground">
                      {p.name}
                    </h3>
                    {showPrice ? (
                      <span className="shrink-0 text-sm font-bold tabular-nums text-primary">
                        {p.price.toFixed(2)} €
                      </span>
                    ) : null}
                  </div>
                  <div
                    className="prose prose-sm mb-3 line-clamp-4 max-w-none flex-1 text-sm text-muted-foreground"
                    dangerouslySetInnerHTML={{
                      __html: p.description || '<p class="italic">Aucune description</p>',
                    }}
                  />
                  {p.videoLink ? (
                    <button
                      type="button"
                      onClick={() => setVideoToPlay(p.videoLink)}
                      className="mt-auto flex items-center gap-1 text-sm font-medium text-primary hover:text-primary/80"
                    >
                      <Icon name="Video" size={14} />
                      Voir la vidéo
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showProductForm ? (
        <ProductFormModal
          onClose={() => setShowProductForm(false)}
          onSubmit={handleAddProduct}
          title="Nouveau produit"
        />
      ) : null}
      {editingProduct ? (
        <ProductFormModal
          onClose={() => setEditingProduct(null)}
          onSubmit={(data) => handleUpdateProduct(editingProduct._id, data)}
          initialData={editingProduct}
          title="Modifier le produit"
        />
      ) : null}
      {videoToPlay ? (
        <VideoModal videoUrl={videoToPlay} onClose={() => setVideoToPlay(null)} />
      ) : null}
    </>
  );
}
